<script setup lang="ts">
import { PhCrosshair, PhMapPin, PhX } from '@phosphor-icons/vue'
import { computed } from 'vue'

import StopSearchField from '@/components/transit/StopSearchField.vue'
import { Button } from '@/components/ui/button'
import type { IndexedStop } from '@/composables/useTransitIndex'

import type { PlannerEndpoint } from './plannerEndpoint'

const props = defineProps<{
  /** 'A' or 'B', matching the mockup's start/end markers. */
  marker: string
  placeholder: string
  stops: IndexedStop[]
  value?: PlannerEndpoint
  open: boolean
  locating: boolean
  /** Set while the map is waiting for a tap for this endpoint. */
  picking: boolean
}>()

const emit = defineEmits<{
  toggle: []
  select: [endpoint: PlannerEndpoint]
  useLocation: []
  startPicking: []
  clear: []
}>()

const stopsById = computed(() => new Map(props.stops.map((stop) => [stop.id, stop])))

function chooseStop(stopId: number) {
  const stop = stopsById.value.get(stopId)
  if (!stop) return
  emit('select', { lat: stop.lat, lng: stop.lng, label: stop.name })
}
</script>

<template>
  <div class="border-b">
    <button
      type="button"
      class="flex w-full items-center gap-3 px-3 py-3 text-left"
      :aria-expanded="open"
      @click="$emit('toggle')"
    >
      <span
        class="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground"
      >
        {{ marker }}
      </span>
      <span class="min-w-0 flex-1 truncate text-sm" :class="value ? '' : 'text-muted-foreground'">
        {{ value?.label ?? placeholder }}
      </span>
      <span
        v-if="value"
        class="shrink-0 text-muted-foreground"
        role="button"
        :aria-label="`Clear ${placeholder}`"
        @click.stop="$emit('clear')"
      >
        <PhX class="size-4" />
      </span>
    </button>

    <div v-if="open" class="space-y-2 border-t bg-muted px-3 py-3">
      <div class="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          type="button"
          class="flex-1 justify-start gap-2"
          :disabled="locating"
          @click="$emit('useLocation')"
        >
          <PhCrosshair class="size-4" />
          {{ locating ? 'Locating…' : 'Use my location' }}
        </Button>
        <Button
          :variant="picking ? 'default' : 'outline'"
          size="sm"
          type="button"
          class="flex-1 justify-start gap-2"
          @click="$emit('startPicking')"
        >
          <PhMapPin class="size-4" />
          {{ picking ? 'Tap the map…' : 'Pick on map' }}
        </Button>
      </div>

      <StopSearchField :stops="stops" placeholder="Search a stop…" @select="chooseStop" />
    </div>
  </div>
</template>
